import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';
import PlaylistCard from './PlaylistCard';
import { countLatestItems, cssClampValue } from '../utils/common';
import { LatestItemTitle, LatestItemCount } from './styled-components';
import ViewAllLink from './ViewAllLink';
import { pages } from '../../config';

const Wrapper = styled.div``;

const Grid = styled.div`
  display: grid;
  gap: ${cssClampValue(10, 20)};
  grid-template-columns: repeat(auto-fill, minmax(105px, 1fr));

  &:focus {
    outline: auto;
    outline-color: var(--earth-fm--color--blue);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

interface Props {
  title?: string;
  className?: string;
}

export default function LatestPlaylists({ title = 'Latest playlists', className = '' }: Props) {
  const data = useStaticQuery<Queries.LatestPlaylistsQuery>(graphql`
    query LatestPlaylists {
      allWpPlaylist(
        sort: { date: DESC }
        filter: {
          playlistSettings: {
            isTrendingRecordingsPlaylist: { ne: true }
            hideFromProduction: { ne: true }
            hideFromPlaylistArchive: { ne: true }
          }
        }
        limit: 12
      ) {
        nodes {
          ...PlaylistCardData
        }
        totalCount
      }
    }
  `);

  const newItemCount = React.useMemo(
    () => countLatestItems(data.allWpPlaylist.nodes),
    [data.allWpPlaylist.nodes],
  );

  const latestItemText = React.useMemo(() => {
    if (newItemCount === 0) {
      return '';
    }
    if (newItemCount === 1) {
      return `We have curated ${newItemCount} extra playlist this month.`;
    }
    return `We have curated ${newItemCount} extra playlists this month.`;
  }, [newItemCount]);

  return (
    <Wrapper className={className}>
      <LatestItemTitle className={`${className}__title`}>{title}</LatestItemTitle>
      <LatestItemCount>{latestItemText}</LatestItemCount>
      <Grid aria-label={title} tabIndex={0}>
        {data.allWpPlaylist.nodes.map((playlist) => (
          <PlaylistCard key={playlist.id} data={playlist} />
        ))}
      </Grid>
      <ViewAllLink
        link={pages.playlists}
      >{`View all ${data.allWpPlaylist.totalCount} playlists`}</ViewAllLink>
    </Wrapper>
  );
}
